// UX-2026-05-27: 自建 i18n 框架 — Provider + useI18n hook + 持久化.
//
// 立场: 不引 react-intl (+45KB) / i18next (+20KB). 字符串当前总量 ~70 条,
// 60 行自建代码足够; 等突破 500 条或要第三语言时再评估迁 i18next.
//
// 用法:
//   const { t, locale, setLocale } = useI18n();
//   t("nav.rooms");                       -> "会话"
//   t("updater.title.found", { version: "0.0.3" })  -> "发现新版本 0.0.3"

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";

import { enUS } from "./en-US";
import { zhCN, type Dict, type DictKey } from "./zh-CN";

export type Locale = "zh-CN" | "en-US";

const STORAGE_KEY = "chatbox.locale";
const DEFAULT_LOCALE: Locale = "zh-CN";
const DICTS: Record<Locale, Dict> = {
  "zh-CN": zhCN,
  "en-US": enUS,
};

interface I18nApi {
  locale: Locale;
  setLocale: (l: Locale) => void;
  t: (key: DictKey, vars?: Record<string, string | number>) => string;
}

const I18nContext = createContext<I18nApi | null>(null);

function detectInitialLocale(): Locale {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved === "zh-CN" || saved === "en-US") return saved;
  } catch {
    /* ignore */
  }
  // 系统语言只在没存过时参考一次, 之后用户显式切则尊重
  if (typeof navigator !== "undefined") {
    const lang = navigator.language.toLowerCase();
    if (lang.startsWith("zh")) return "zh-CN";
    if (lang.startsWith("en")) return "en-US";
  }
  return DEFAULT_LOCALE;
}

export function I18nProvider({ children }: { children: ReactNode }) {
  const [locale, setLocaleState] = useState<Locale>(() => detectInitialLocale());

  // 跨标签页同步 (web 端): storage 事件
  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key === STORAGE_KEY && (e.newValue === "zh-CN" || e.newValue === "en-US")) {
        setLocaleState(e.newValue);
      }
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  const setLocale = useCallback((l: Locale) => {
    setLocaleState(l);
    try {
      localStorage.setItem(STORAGE_KEY, l);
    } catch {
      /* ignore */
    }
  }, []);

  const t = useCallback(
    (key: DictKey, vars?: Record<string, string | number>): string => {
      // 主字典 → fallback 中文 → fallback key 本身
      const dict = DICTS[locale] ?? zhCN;
      let s = dict[key] ?? zhCN[key] ?? (key as string);
      if (vars) {
        for (const [k, v] of Object.entries(vars)) {
          s = s.replace(new RegExp(`\\{${k}\\}`, "g"), String(v));
        }
      }
      return s;
    },
    [locale],
  );

  const api = useMemo<I18nApi>(() => ({ locale, setLocale, t }), [locale, setLocale, t]);
  return <I18nContext.Provider value={api}>{children}</I18nContext.Provider>;
}

export function useI18n(): I18nApi {
  const ctx = useContext(I18nContext);
  if (!ctx) throw new Error("useI18n 必须在 <I18nProvider> 内使用");
  return ctx;
}

// 给 type-aware 场景导出 (Composer 写死的字符串可以编译期校验):
export type { DictKey };
